import { Box, Collapse, Fade, IconButton, Paper, Typography } from "@mui/material"
import { KeyboardArrowDownOutlined, KeyboardArrowUpOutlined } from "@mui-symbols-material/w300"
import { useSearch } from "@tanstack/react-router"
import type { FC } from "react"
import { useRef, useState } from "react"

import type { PriceChangeSubmitContext, PriceChangeTablesHandle } from "../components/PriceChangeTables"
import { PriceChangeTables } from "../components/PriceChangeTables"

import BulkUpdateOptions from "@/screens/Pricing/components/BulkUpdateOptions"
import { AddSpecialPriceDialog, SubmitPricingDialog } from "@/screens/Pricing/components/dialogs"
import PriceChangeFilterChips from "@/screens/Pricing/components/filterChips/PriceChangeFilterChips"
import PriceChangeFilter from "@/screens/Pricing/components/PriceChangeFilter"
import { PricingDialogs } from "@/screens/Pricing/constants"
import { usePricingDialog } from "@/screens/Pricing/hooks/usePricingDialog.tsx"

const PriceChangeContainer: FC = () => {
  const search = useSearch({
    from: "/app/pricing/_layout/price-change",
    strict: true,
  })

  const hasFilterParams = Boolean(Object.keys(search).length)

  const [isFilterOpen, setIsFilterOpen] = useState<boolean>(true)
  const [isFilterChipsOpen, setIsFilterChipsOpen] = useState<boolean>(false)
  const [isBulkUpdateOpen, setIsBulkUpdateOpen] = useState<boolean>(false)

  const { isDialogOpen, setIsDialogOpen, setSelectedProduct, selectedProduct } = usePricingDialog()
  const tablesRef = useRef<PriceChangeTablesHandle>(null)
  const [submitContext, setSubmitContext] = useState<PriceChangeSubmitContext | null>(null)

  const handleDialogClose = () => {
    setIsDialogOpen(null)
    setSelectedProduct(null)
  }

  const handleSubmitDialogOpen = () => {
    const context = tablesRef.current?.getSubmitContext()
    if (!context) {
      return
    }
    setSubmitContext(context)
    setIsDialogOpen(PricingDialogs.SubmitPriceChange)
  }

  return (
    <Box className='flex flex-col gap-6'>
      {/*  Filter section */}
      <Paper className='flex flex-1 flex-col border border-gray-400 bg-gray-100 p-6' elevation={0}>
        <Box className='flex items-start justify-between'>
          <Typography variant='h6' className='font-normal text-gray-800'>
            Filters
          </Typography>
          <Box className={isFilterOpen ? "mb-6" : ""}>
            <IconButton
              onClick={() => setIsFilterOpen(!isFilterOpen)}
              aria-expanded={isFilterOpen}
              aria-label='show more'
            >
              {isFilterOpen ? <KeyboardArrowUpOutlined /> : <KeyboardArrowDownOutlined />}
            </IconButton>
          </Box>
        </Box>

        <Collapse in={isFilterOpen}>
          <PriceChangeFilter />
        </Collapse>
      </Paper>

      {/*  Filter Chips */}
      <Paper className='flex flex-1 flex-col border border-gray-400 bg-gray-100 p-6' elevation={0}>
        <Box className='flex items-start justify-between'>
          <Typography variant='h6' className='font-normal text-gray-800'>
            Applied Filters
          </Typography>
          <Box className={isFilterChipsOpen ? "mb-6" : ""}>
            <IconButton
              onClick={() => setIsFilterChipsOpen(!isFilterChipsOpen)}
              aria-expanded={isFilterChipsOpen}
              aria-label='show more'
            >
              {isFilterChipsOpen ? <KeyboardArrowUpOutlined /> : <KeyboardArrowDownOutlined />}
            </IconButton>
          </Box>
        </Box>

        <Collapse in={isFilterChipsOpen}>
          <PriceChangeFilterChips />
        </Collapse>
      </Paper>

      {/*  Bulk Update Options */}
      <Fade in={hasFilterParams} unmountOnExit>
        <Paper className='flex flex-1 flex-col border border-gray-400 bg-gray-100 p-6' elevation={0}>
          <Box className='flex items-start justify-between'>
            <Typography variant='h6' className='font-normal text-gray-800'>
              Bulk Update Options
            </Typography>
            <Box className={isBulkUpdateOpen ? "mb-6" : ""}>
              <IconButton
                onClick={() => setIsBulkUpdateOpen(!isBulkUpdateOpen)}
                aria-expanded={isBulkUpdateOpen}
                aria-label='show more'
              >
                {isBulkUpdateOpen ? <KeyboardArrowUpOutlined /> : <KeyboardArrowDownOutlined />}
              </IconButton>
            </Box>
          </Box>

          <Collapse in={isBulkUpdateOpen}>
            <BulkUpdateOptions />
          </Collapse>
        </Paper>
      </Fade>

      <PriceChangeTables
        ref={tablesRef}
        setDialogOpen={setIsDialogOpen}
        openSubmitDialog={handleSubmitDialogOpen}
        setSelectedProduct={setSelectedProduct}
      />

      {isDialogOpen === PricingDialogs.SubmitPriceChange && submitContext && (
        <SubmitPricingDialog submitContext={submitContext} onClose={handleDialogClose} />
      )}

      {isDialogOpen === PricingDialogs.AddSpecialPrice && selectedProduct && (
        <AddSpecialPriceDialog
          onSuccess={handleDialogClose}
          onClose={handleDialogClose}
          selectedProduct={selectedProduct}
        />
      )}
    </Box>
  )
}

export default PriceChangeContainer
